/**
 * Etapa 2 — Organização e levantamento: como o trabalho é organizado (norma, modo, tempo, ritmo),
 * o quadro de cargas levantadas (gravado na hora) e o movimento e o esforço exigidos.
 */
import React, { useCallback, useState } from 'react';
import { Alert, Box, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicLoad } from '../../../../../types/ergonomicWorkstation';
import { ConfirmDialog } from '../dialogs/ItemDialog';
import { LoadDialog } from '../dialogs/LoadDialog';
import { ItemsBlock } from '../ItemsBlock';
import { useItems } from '../useItems';
import type { WizardStepProps, WorkstationDraft } from '../Wizard.types';
import { errorMessage } from '../wizardValidation';
import { WorkstationField } from '../WorkstationField';

const fieldSx = { '& .MuiFormHelperText-root': { fontSize: '1rem' } };

const formatWeight = (weightKg: number | null): string =>
  weightKg === null ? '—' : `${String(Number(weightKg)).replace('.', ',')} kg`;

export const OrganizationStep: React.FC<WizardStepProps> = ({
  workstation,
  options,
  values,
  errors,
  canEdit,
  disabled,
  onChange,
  onSaved,
}) => {
  const workstationId = workstation.id;
  const loadLoads = useCallback(() => ergonomicWorkstationService.getLoads(workstationId), [workstationId]);
  const loads = useItems(loadLoads, 'Não foi possível carregar as cargas.');
  const [dialog, setDialog] = useState<{ item: ErgonomicLoad | null } | null>(null);
  const [removing, setRemoving] = useState<ErgonomicLoad | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const common = { values, errors, onChange, canEdit, disabled };

  const confirmRemoval = async () => {
    if (!removing) return;
    try {
      await ergonomicWorkstationService.deleteLoad(workstationId, removing.id);
      setRemoving(null);
      await loads.reload();
      onSaved('Carga removida.');
    } catch (err: unknown) {
      setRemoving(null);
      setRemoveError(errorMessage(err, 'Não foi possível remover a carga.'));
    }
  };

  const closeDialogSaved = async (message: string) => {
    setDialog(null);
    await loads.reload();
    onSaved(message);
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Conte como o trabalho é feito e o que a pessoa levanta. Tudo aqui é opcional: preencha o que você observou.
      </Typography>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Organização do trabalho
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <WorkstationField field="productionNorm" label="Norma de produção" multiline help="Como a produção é combinada e acompanhada." {...common} />
        <WorkstationField field="operatingMode" label="Modo operatório" multiline help="Como a tarefa é executada, passo a passo." {...common} />
        <WorkstationField field="timeRequirement" label="Existência de tempo" md={6} help="Ex.: Não há exigência de tempo para o processo." {...common} />
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            select
            fullWidth
            label="Ritmo de trabalho"
            value={values.workPace}
            onChange={(event) => onChange({ workPace: event.target.value as WorkstationDraft['workPace'] })}
            error={Boolean(errors.workPace)}
            helperText={errors.workPace}
            disabled={disabled || !canEdit}
            sx={fieldSx}
          >
            <MenuItem value="">Não informado</MenuItem>
            {(options?.workPaces ?? []).map((pace) => (
              <MenuItem key={pace.value} value={pace.value}>
                {pace.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <WorkstationField field="cycleMinutes" label="Tempo cronometrado (min)" md={3} inputMode="decimal" help="Por produto. Ex.: 12" {...common} />
        <WorkstationField field="taskContent" label="Conteúdo das tarefas" multiline md={6} help="Ex.: Operação de montagem da suspensão." {...common} />
        <WorkstationField field="cognitiveAspect" label="Aspecto cognitivo" multiline md={6} help="Atenção, decisão e pressão mental exigidas." {...common} />
      </Grid>

      {removeError && (
        <Alert severity="error" sx={{ fontSize: '1rem', mb: 2 }} onClose={() => setRemoveError(null)}>
          {removeError}
        </Alert>
      )}
      <ItemsBlock<ErgonomicLoad>
        title="Levantamento e transporte de cargas"
        description="Cada material ou componente que a pessoa levanta ou carrega no posto."
        items={loads.items}
        loading={loads.loading}
        error={loads.error}
        onRetry={loads.reload}
        columns={[
          { id: 'component', label: 'Material ou componente', primary: true, render: (row) => row.component },
          { id: 'weight', label: 'Peso', align: 'right', render: (row) => formatWeight(row.weightKg) },
          { id: 'dimension', label: 'Dimensão', hideOnMobile: true, render: (row) => row.dimension ?? '—' },
          { id: 'frequency', label: 'Frequência', hideOnMobile: true, render: (row) => row.frequency ?? '—' },
          { id: 'distance', label: 'Distância', hideOnMobile: true, render: (row) => row.distance ?? '—' },
          { id: 'method', label: 'Método utilizado', hideOnMobile: true, render: (row) => row.method ?? '—' },
        ]}
        emptyState={{
          title: 'Nenhuma carga registrada neste posto',
          description: 'Use "Adicionar carga" para cada material ou componente levantado. Se a pessoa não levanta nada, siga em frente.',
        }}
        addLabel="Adicionar carga"
        itemName="carga"
        itemLabel={(row) => row.component}
        canEdit={canEdit}
        onAdd={() => setDialog({ item: null })}
        onEdit={(row) => setDialog({ item: row })}
        onRemove={setRemoving}
      />

      <Grid container spacing={3}>
        <WorkstationField field="movementRequired" label="Movimento exigido" md={6} help="Ex.: Mãos, braços, coluna, pernas" {...common} />
        <WorkstationField field="effortRequired" label="Esforço exigido" md={6} help="Ex.: Empurrar e levantar" {...common} />
      </Grid>

      {dialog && (
        <LoadDialog workstationId={workstationId} item={dialog.item} onSaved={closeDialogSaved} onClose={() => setDialog(null)} />
      )}
      {removing && (
        <ConfirmDialog
          title="Remover esta carga?"
          message="Ela some do cadastro do posto e do documento."
          onConfirm={confirmRemoval}
          onCancel={() => setRemoving(null)}
        />
      )}
    </Box>
  );
};

export default OrganizationStep;
